import React, { useEffect, useState } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Panel from 'components/Infos/Panel'
import { PageWrapper, ContentWrapperLarge, StyledIcon } from 'components/Infos/index'
import { AutoRow, RowBetween, RowFixed } from 'components/Row'
import Column, { AutoColumn } from 'components/Column'
import { ButtonLight, ButtonDark } from 'components/Infos/ButtonStyled'
import PairChart from 'components/Infos/PairChart'
import Link, { InnerLink } from 'components/Infos/Link'
import TxnList from 'components/Infos/TxnList'
import Loader from 'components/Loading'
import { BasicLink } from 'components/Infos/Link'
import { formattedNum, formattedPercent, getPoolLink, getSwapLink } from 'utils/infos'
import { usePairData, usePairTransactions } from 'hooks/infos/PairData'
import { TYPE } from 'theme'
import CopyHelper from 'components/Infos/Copy'
import { useMedia } from 'react-use'
import DoubleTokenLogo from 'components/Infos/DoubleLogo'
import TokenLogo from 'components/TokenImage'
import { Hover } from 'components/Infos/index'
import { useMetisPrice } from 'hooks/infos/GlobalData'
import Warning from 'components/Infos/Warning'
import { usePathDismissed, useSavedPairs } from 'hooks/infos/LocalStorage'

import { Bookmark, PlusCircle } from 'react-feather'
import FormattedName from 'components/Infos/FormattedName'
import { useExistedTokens } from 'hooks/infos/Application'
import QuestionHelper from 'components/Infos/QuestionHelper'
import { OptionButton } from 'components/Infos/ButtonStyled'

const DashboardWrapper = styled.div`
  width: 100%;
`

const PanelWrapper = styled.div`
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: max-content;
  gap: 6px;
  display: inline-grid;
  width: 100%;
  align-items: start;
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    > * {
      grid-column: 1 / 4;
    }

    > * {
      &:first-child {
        width: 100%;
      }
    }
  }
`

const TokenDetailsLayout = styled.div`
  display: inline-grid;
  width: 100%;
  grid-template-columns: auto auto auto auto 1fr;
  column-gap: 60px;
  align-items: start;

  &:last-child {
    align-items: center;
    justify-items: end;
  }
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    > * {
      grid-column: 1 / 4;
      margin-bottom: 1rem;
    }

    &:last-child {
      align-items: start;
      justify-items: start;
    }
  }
`

const FixedPanel = styled(Panel)`
  width: fit-content;
  padding: 8px 12px;
  border-radius: 10px;

  :hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.bg2};
  }
`

const HoverSpan = styled.span`
  :hover {
    cursor: pointer;
    opacity: 0.7;
  }
`

const WarningGrouping = styled.div<{ disabled?: boolean }>`
  opacity: ${({ disabled }) => disabled && '0.4'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`

function PairPage({
  match,
  history
}: RouteComponentProps<{
  pairAddress: string
}>) {
  const { t } = useTranslation()
  const SCAN_URL = process.env.REACT_APP_SCAN_URL
  const [viewVolType, setViewVolType] = useState('24h')
  const pairAddress = match.params.pairAddress.toLowerCase()
  const {
    token0,
    token1,
    reserve0,
    reserve1,
    reserveUSD,
    trackedReserveUSD,
    oneDayVolumeUSD,
    oneWeekVolumeUSD,
    volumeChangeUSD,
    weekVolumeChangeUSD,
    oneDayVolumeUntracked,
    oneWeekVolumeUntracked,
    volumeChangeUntracked,
    weekVolumeChangeUntracked,
    liquidityChangeUSD,
    apr
  } = usePairData(pairAddress)

  useEffect(() => {
    document.querySelector('body')?.scrollTo(0, 0)
  }, [])

  const transactions = usePairTransactions(pairAddress)
  // liquidity
  const liquidity = trackedReserveUSD
    ? formattedNum(trackedReserveUSD, true)
    : reserveUSD
    ? formattedNum(parseFloat(reserveUSD), true)
    : '-'
  const liquidityChange = formattedPercent(liquidityChangeUSD)

  // mark if using untracked liquidity
  const [usingTracked, setUsingTracked] = useState(true)
  useEffect(() => {
    setUsingTracked(!trackedReserveUSD ? false : true)
  }, [trackedReserveUSD])

  // volume	  // volume
  const volume =
    oneDayVolumeUSD || oneDayVolumeUSD === 0
      ? formattedNum(oneDayVolumeUSD === 0 ? oneDayVolumeUntracked : oneDayVolumeUSD, true)
      : oneDayVolumeUSD === 0
      ? '$0'
      : '-'

  const weekVolume =
    oneWeekVolumeUSD || oneWeekVolumeUSD === 0
      ? formattedNum(oneWeekVolumeUSD === 0 ? oneWeekVolumeUntracked : oneWeekVolumeUSD, true)
      : oneWeekVolumeUSD === 0
      ? '$0'
      : '-'

  // mark if using untracked volume
  const [usingUtVolume, setUsingUtVolume] = useState(false)
  useEffect(() => {
    setUsingUtVolume(oneDayVolumeUSD === 0 ? true : false)
  }, [oneDayVolumeUSD])

  const volumeChange = formattedPercent(!usingUtVolume ? volumeChangeUSD : volumeChangeUntracked)
  const weekVolumeChange = formattedPercent(!usingUtVolume ? weekVolumeChangeUSD : weekVolumeChangeUntracked)

  // get fees	  // get fees
  const fees =
    oneDayVolumeUSD || oneDayVolumeUSD === 0
      ? usingUtVolume
        ? formattedNum(oneDayVolumeUntracked * 0.003, true)
        : formattedNum(oneDayVolumeUSD * 0.003, true)
      : '-'

  const lpFees =
    oneDayVolumeUSD || oneDayVolumeUSD === 0
      ? usingUtVolume
        ? formattedNum(oneDayVolumeUntracked * 0.0025, true)
        : formattedNum(oneDayVolumeUSD * 0.0025, true)
      : '-'

  const oneWeekFees =
    oneWeekVolumeUSD || oneWeekVolumeUSD === 0
      ? usingUtVolume
        ? formattedNum(oneDayVolumeUntracked * 0.003, true)
        : formattedNum(oneWeekVolumeUSD * 0.003, true)
      : '-'

  const oneWeekLpFees =
    oneWeekVolumeUSD || oneWeekVolumeUSD === 0
      ? usingUtVolume
        ? formattedNum(oneWeekVolumeUntracked * 0.0025, true)
        : formattedNum(oneWeekVolumeUSD * 0.0025, true)
      : '-'

  const aprFormatted = formattedPercent(apr, false)
  // token data for usd
  const [metisPrice] = useMetisPrice()
  const token0USD =
    token0?.derivedMETIS && metisPrice ? formattedNum(parseFloat(token0.derivedMETIS) * metisPrice, true) : ''

  const token1USD =
    token1?.derivedMETIS && metisPrice ? formattedNum(parseFloat(token1.derivedMETIS) * metisPrice, true) : ''

  // rates
  const token0Rate = reserve0 && reserve1 ? formattedNum(parseFloat(reserve1) / parseFloat(reserve0)) : '-'
  const token1Rate = reserve0 && reserve1 ? formattedNum(parseFloat(reserve0) / parseFloat(reserve1)) : '-'

  // formatted symbols for overflow
  const formattedSymbol0 = token0?.symbol.length > 6 ? token0?.symbol.slice(0, 5) + '...' : token0?.symbol
  const formattedSymbol1 = token1?.symbol.length > 6 ? token1?.symbol.slice(0, 5) + '...' : token1?.symbol

  const below1080 = useMedia('(max-width: 1080px)')
  const below900 = useMedia('(max-width: 900px)')

  const [dismissed, markAsDismissed] = usePathDismissed(history.location.pathname)

  useEffect(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0
    })
  }, [])

  const [savedPairs, addPair] = useSavedPairs()

  const listedTokens = useExistedTokens()

  return (
    <PageWrapper>
      <span />
      <Warning
        type={'pair'}
        show={!!(!dismissed && listedTokens && !(listedTokens[token0?.id] && listedTokens[token1?.id]))}
        setShow={(val: boolean) => markAsDismissed()}
        address={pairAddress}
      />
      <ContentWrapperLarge>
        <RowBetween>
          <TYPE.title fontSize={26}>
            <BasicLink to="/analytics/pairs">
              <i className="iconfont icon-page-prev" style={{ fontSize: 26, marginRight: 24 }} />
              {t('analytics.pairs')}
            </BasicLink>
          </TYPE.title>
        </RowBetween>
        <WarningGrouping
          disabled={!!(!dismissed && listedTokens && !(listedTokens[token0?.id] && listedTokens[token1?.id]))}
        >
          <DashboardWrapper>
            <AutoColumn gap="40px" style={{ marginBottom: '1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  width: '100%'
                }}
              >
                <RowFixed style={{ flexWrap: 'wrap', minWidth: '100px' }}>
                  <RowFixed>
                    {token0 && token1 && (
                      <DoubleTokenLogo a0={token0?.id || ''} a1={token1?.id || ''} size={32} margin={true} />
                    )}{' '}
                    <TYPE.v2Main fontSize={below1080 ? '1.5rem' : '2rem'} style={{ margin: '0 1rem' }}>
                      {token0 && token1 ? (
                        <>
                          <HoverSpan onClick={() => history.push(`/analytics/tokens/${token0?.id}`)}>
                            {token0.symbol}
                          </HoverSpan>
                          <span>-</span>
                          <HoverSpan onClick={() => history.push(`/analytics/tokens/${token1?.id}`)}>
                            {token1.symbol}
                          </HoverSpan>{' '}
                          Pair
                        </>
                      ) : (
                        ''
                      )}
                    </TYPE.v2Main>
                  </RowFixed>
                </RowFixed>
                <RowFixed
                  ml={below900 ? '0' : '2.5rem'}
                  mt={below1080 ? '1rem' : ''}
                  style={{
                    flexDirection: below1080 ? 'row-reverse' : 'initial',
                    alignItems: 'center'
                  }}
                >
                  {!!!savedPairs[pairAddress] && !below1080 ? (
                    <Hover
                      onClick={() =>
                        addPair({
                          address: pairAddress,
                          token0Address: token0.id,
                          token1Address: token1.id,
                          token0Symbol: token0.symbol,
                          token1Symbol: token1.symbol
                        })
                      }
                    >
                      <StyledIcon>
                        <PlusCircle style={{ marginRight: '0.5rem' }} />
                      </StyledIcon>
                    </Hover>
                  ) : !below1080 ? (
                    <StyledIcon>
                      <Bookmark style={{ marginRight: '0.5rem', opacity: 0.4 }} />
                    </StyledIcon>
                  ) : (
                    <></>
                  )}

                  <InnerLink to={getPoolLink(token0?.id, token1?.id)}>
                    <ButtonLight>+ {t('analytics.add_liquidity')}</ButtonLight>
                  </InnerLink>
                  <InnerLink to={getSwapLink(token0?.id, token1?.id)}>
                    <ButtonDark ml={!below1080 ? '.5rem' : ''} mr={below1080 ? '.5rem' : ''}>
                      {t('analytics.trade')}
                    </ButtonDark>
                  </InnerLink>
                </RowFixed>
              </div>
            </AutoColumn>
            <AutoRow
              gap="6px"
              style={{
                width: 'fit-content',
                marginTop: below900 ? '1rem' : '0',
                marginBottom: below900 ? '0' : '2rem',
                flexWrap: 'wrap'
              }}
            >
              <FixedPanel onClick={() => history.push(`/analytics/tokens/${token0?.id}`)}>
                <RowFixed>
                  <TokenLogo address={token0?.id} size={'16px'} />
                  <TYPE.mainLg fontSize={'16px'} lineHeight={1} fontWeight={500} ml={'4px'}>
                    {token0 && token1
                      ? `1 ${formattedSymbol0} = ${token0Rate} ${formattedSymbol1} ${
                          parseFloat(token0?.derivedMETIS) ? '(' + token0USD + ')' : ''
                        }`
                      : '-'}
                  </TYPE.mainLg>
                </RowFixed>
              </FixedPanel>
              <FixedPanel onClick={() => history.push(`/analytics/tokens/${token1?.id}`)}>
                <RowFixed>
                  <TokenLogo address={token1?.id} size={'16px'} />
                  <TYPE.mainLg fontSize={'16px'} lineHeight={1} fontWeight={500} ml={'4px'}>
                    {token0 && token1
                      ? `1 ${formattedSymbol1} = ${token1Rate} ${formattedSymbol0}  ${
                          parseFloat(token1?.derivedMETIS) ? '(' + token1USD + ')' : ''
                        }`
                      : '-'}
                  </TYPE.mainLg>
                </RowFixed>
              </FixedPanel>
            </AutoRow>
            <>
              {!below1080 && <TYPE.title fontSize={'1.125rem'}>Pair Stats</TYPE.title>}
              <PanelWrapper style={{ marginTop: '1.5rem' }}>
                <Panel style={{ height: '100%' }}>
                  <AutoRow style={{ alignItems: 'flex-start', margin: 0 }}>
                    <AutoColumn gap="20px" style={{ flex: 1 }}>
                      <RowBetween>
                        <TYPE.v2Main>
                          {t('analytics.total_liquidity')} {!usingTracked ? `(${t('analytics.untracked')})` : ''}
                        </TYPE.v2Main>
                        <div />
                      </RowBetween>
                      <RowBetween align="flex-end">
                        <TYPE.mainLg fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                          {liquidity}
                        </TYPE.mainLg>
                      </RowBetween>
                      <RowBetween>
                        <TYPE.mainLg>{liquidityChange}</TYPE.mainLg>
                      </RowBetween>
                    </AutoColumn>

                    <AutoColumn gap="20px" style={{ flex: 1 }}>
                      <RowBetween>
                        <TYPE.v2Main>
                          {t('analytics.lp_reward_apr')} {!usingTracked ? `(${t('analytics.untracked')})` : ''}
                        </TYPE.v2Main>
                        <QuestionHelper text={t('analytics.pairs_list.base_24hr_annualized')} />
                        <div />
                      </RowBetween>
                      <RowBetween align="flex-end">
                        <TYPE.mainLg fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                          {aprFormatted}
                        </TYPE.mainLg>
                      </RowBetween>
                    </AutoColumn>
                  </AutoRow>
                </Panel>
                <Panel style={{ height: '100%' }}>
                  <AutoColumn>
                    <AutoRow gap="5px">
                      <OptionButton
                        active={viewVolType === '24h'}
                        onClick={() => {
                          setViewVolType('24h')
                        }}
                      >
                        {t('analytics.24h')}
                      </OptionButton>
                      <OptionButton
                        active={viewVolType === '7d'}
                        onClick={() => {
                          setViewVolType('7d')
                        }}
                      >
                        {t('analytics.7d')}
                      </OptionButton>
                    </AutoRow>
                    <AutoRow style={{ marginTop: '20px', alignItems: 'flex-start' }}>
                      <AutoColumn gap="20px" style={{ flex: 1 }}>
                        <RowBetween>
                          <TYPE.v2Main>
                            {viewVolType === '24h' ? t('analytics.volume_24hrs') : t('analytics.volume_7d')}{' '}
                            {usingUtVolume && `(${t('analytics.untracked')})`}
                          </TYPE.v2Main>
                          <div />
                        </RowBetween>
                        <RowBetween align="flex-end">
                          <TYPE.v2Main fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                            {viewVolType === '24h' ? volume : weekVolume}
                          </TYPE.v2Main>
                        </RowBetween>
                        <RowBetween>
                          <TYPE.v2Main>{viewVolType === '24h' ? volumeChange : weekVolumeChange}</TYPE.v2Main>
                        </RowBetween>
                      </AutoColumn>
                      <AutoColumn gap="20px" style={{ flex: 1 }}>
                        <RowBetween>
                          <TYPE.v2Main>
                            {viewVolType === '24h' ? t('analytics.lp_fees_24hrs') : t('analytics.lp_fees_7d')}
                          </TYPE.v2Main>
                          <div />
                        </RowBetween>
                        <RowBetween align="flex-end">
                          <TYPE.v2Main fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                            {viewVolType === '24h' ? lpFees : oneWeekLpFees}
                          </TYPE.v2Main>
                        </RowBetween>
                        <RowBetween>
                          <TYPE.v2Main>
                            {t('analytics.out_of_fees', { fees: viewVolType === '24h' ? fees : oneWeekFees })}
                          </TYPE.v2Main>
                        </RowBetween>
                      </AutoColumn>
                    </AutoRow>
                  </AutoColumn>
                </Panel>

                <Panel style={{ height: '100%' }}>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.v2Main>{t('analytics.pooled_tokens')}</TYPE.v2Main>
                      <div />
                    </RowBetween>
                    <Hover onClick={() => history.push(`/analytics/tokens/${token0?.id}`)} fade={true}>
                      <AutoRow gap="4px">
                        <TokenLogo address={token0?.id} />
                        <TYPE.mainLg fontSize={20} lineHeight={1} fontWeight={500}>
                          <RowFixed>
                            {reserve0 ? formattedNum(parseFloat(reserve0)) : ''}{' '}
                            <FormattedName text={token0?.symbol ?? ''} maxCharacters={8} margin={true} />
                          </RowFixed>
                        </TYPE.mainLg>
                      </AutoRow>
                    </Hover>
                    <Hover onClick={() => history.push(`/analytics/tokens/${token1?.id}`)} fade={true}>
                      <AutoRow gap="4px">
                        <TokenLogo address={token1?.id} />
                        <TYPE.mainLg fontSize={20} lineHeight={1} fontWeight={500}>
                          <RowFixed>
                            {reserve1 ? formattedNum(parseFloat(reserve1)) : ''}{' '}
                            <FormattedName text={token1?.symbol ?? ''} maxCharacters={8} margin={true} />
                          </RowFixed>
                        </TYPE.mainLg>
                      </AutoRow>
                    </Hover>
                  </AutoColumn>
                </Panel>
                <Panel
                  style={{
                    gridColumn: below1080 ? '1' : '2/4',
                    gridRow: below1080 ? '' : '1/4'
                  }}
                >
                  <PairChart
                    address={pairAddress}
                    base0={parseFloat(reserve1) / parseFloat(reserve0)}
                    base1={parseFloat(reserve0) / parseFloat(reserve1)}
                  />
                </Panel>
              </PanelWrapper>
              <TYPE.v2Main fontSize={'1.125rem'} style={{ marginTop: '3rem' }}>
                {t('analytics.transactions')}
              </TYPE.v2Main>{' '}
              <Panel
                style={{
                  marginTop: '1.5rem'
                }}
              >
                {transactions ? <TxnList transactions={transactions} /> : <Loader show={true} />}
              </Panel>
              <RowBetween style={{ marginTop: '3rem' }}>
                <TYPE.v2Main fontSize={'1.125rem'}>{t('analytics.pair_info')}</TYPE.v2Main>{' '}
              </RowBetween>
              <Panel
                rounded
                style={{
                  marginTop: '1.5rem'
                }}
                p={20}
              >
                <TokenDetailsLayout>
                  <Column>
                    <TYPE.v2Main>{t('analytics.pair_name')}</TYPE.v2Main>
                    <TYPE.v2Main style={{ marginTop: '.5rem' }}>
                      <RowFixed>
                        <FormattedName text={token0?.symbol ?? ''} maxCharacters={8} />
                        -
                        <FormattedName text={token1?.symbol ?? ''} maxCharacters={8} />
                      </RowFixed>
                    </TYPE.v2Main>
                  </Column>
                  <Column>
                    <TYPE.v2Main>{t('analytics.pair_address')}</TYPE.v2Main>
                    <AutoRow align="flex-end">
                      <TYPE.v2Main style={{ marginTop: '.5rem' }}>
                        {pairAddress.slice(0, 6) + '...' + pairAddress.slice(38, 42)}
                      </TYPE.v2Main>
                      <CopyHelper toCopy={pairAddress} />
                    </AutoRow>
                  </Column>
                  <Column>
                    <TYPE.v2Main>
                      <RowFixed>
                        <FormattedName text={token0?.symbol ?? ''} maxCharacters={8} />{' '}
                        <span style={{ marginLeft: '4px' }}>{t('analytics.address')}</span>
                      </RowFixed>
                    </TYPE.v2Main>
                    <AutoRow align="flex-end">
                      <TYPE.v2Main style={{ marginTop: '.5rem' }}>
                        {token0 && token0.id.slice(0, 6) + '...' + token0.id.slice(38, 42)}
                      </TYPE.v2Main>
                      <CopyHelper toCopy={token0?.id} />
                    </AutoRow>
                  </Column>
                  <Column>
                    <TYPE.v2Main>
                      <RowFixed>
                        <FormattedName text={token1?.symbol ?? ''} maxCharacters={8} />{' '}
                        <span style={{ marginLeft: '4px' }}>{t('analytics.address')}</span>
                      </RowFixed>
                    </TYPE.v2Main>
                    <AutoRow align="flex-end">
                      <TYPE.v2Main style={{ marginTop: '.5rem' }} fontSize={16}>
                        {token1 && token1.id.slice(0, 6) + '...' + token1.id.slice(38, 42)}
                      </TYPE.v2Main>
                      <CopyHelper toCopy={token1?.id} />
                    </AutoRow>
                  </Column>
                  <ButtonLight>
                    <Link external href={`${SCAN_URL}/address/${pairAddress}`}>
                      {t('analytics.view_on_metis_scan')} ↗
                    </Link>
                  </ButtonLight>
                </TokenDetailsLayout>
              </Panel>
            </>
          </DashboardWrapper>
        </WarningGrouping>
      </ContentWrapperLarge>
    </PageWrapper>
  )
}

export default withRouter(PairPage)
